if (!customElements.get('cart-special-instruction')) {
  const CHECKOUT_SELECTOR =
    '#checkout, #CartDrawer-Checkout, button[name="checkout"].cart__checkout-button';
  const STORAGE_KEY = 'cartSpecialInstructionChecked';

  customElements.define(
    'cart-special-instruction',
    class CartSpecialInstruction extends HTMLElement {
      static STORAGE_KEY = STORAGE_KEY;
      static instances = new Set();
      static instructionChecked = sessionStorage.getItem(STORAGE_KEY) === 'true';
      static checkoutListenerAttached = false;
      static cartUpdateUnsubscriber = undefined;

      static hasNominationBraceletInCart() {
        const cartItems = document.querySelectorAll(
          '#main-cart-items .cart-item, #CartDrawer-CartItems .cart-item'
        );

        for (const item of cartItems) {
          const vendor = (item.getAttribute('data-product-vendor') || '').trim().toUpperCase();
          const tags = (item.getAttribute('data-product-tags') || '')
            .split(',')
            .map((tag) => tag.trim().toUpperCase());

          if (vendor === 'NOMINATION' && (tags.includes('BRACELETS') || tags.includes('BRACELET'))) {
            return true;
          }
        }

        return false;
      }

      static syncNominationPrompt() {
        const hasNomination = CartSpecialInstruction.hasNominationBraceletInCart();

        document.querySelectorAll('[data-nomination-prompt]').forEach((prompt) => {
          prompt.hidden = !hasNomination;
        });

        document.querySelectorAll('cart-special-instruction').forEach((instance) => {
          instance.dataset.nominationBraceletCart = hasNomination ? 'true' : 'false';
        });

        if (!hasNomination) {
          CartSpecialInstruction.clearCheckedState();
        }
      }

      static clearCheckedState() {
        CartSpecialInstruction.instructionChecked = false;
        sessionStorage.removeItem(STORAGE_KEY);

        CartSpecialInstruction.forEachInstance((instance) => {
          if (instance.checkbox) {
            instance.checkbox.checked = false;
          }

          instance.resetTextareaAttributes();
          instance.hideError();

          // Cart page note stays visible; only collapse the drawer note.
          if (instance.dataset.context === 'drawer') {
            instance.collapseAndBlur();
          } else {
            instance.ensureNoteVisible();
          }
        });
      }

      static restoreCheckedState({ focus = false, focusContext } = {}) {
        if (!CartSpecialInstruction.instructionChecked || !CartSpecialInstruction.hasNominationBraceletInCart()) {
          CartSpecialInstruction.ensurePageNoteVisible();
          return;
        }

        CartSpecialInstruction.forEachInstance((instance) => {
          if (instance.checkbox) {
            instance.checkbox.checked = true;
          }

          instance.resetTextareaAttributes();
          instance.hideError();
          instance.ensureNoteVisible();
        });

        if (!focus) {
          return;
        }

        const targetInstance = focusContext
          ? CartSpecialInstruction.getInstance(focusContext)
          : CartSpecialInstruction.instances.values().next().value;

        requestAnimationFrame(() => {
          targetInstance?.textarea?.focus();
        });
      }

      static setupCartUpdateListener() {
        if (CartSpecialInstruction.cartUpdateUnsubscriber || typeof subscribe !== 'function') {
          return;
        }

        if (typeof PUB_SUB_EVENTS === 'undefined') {
          return;
        }

        CartSpecialInstruction.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          requestAnimationFrame(() => {
            CartSpecialInstruction.syncNominationPrompt();
            CartSpecialInstruction.restoreCheckedState();
            CartSpecialInstruction.ensurePageNoteVisible();
          });
        });
      }

      static ensurePageNoteVisible() {
        CartSpecialInstruction.forEachInstance((instance) => {
          if (instance.dataset.context === 'page') {
            instance.ensureNoteVisible();
          }
        });
      }

      connectedCallback() {
        this.checkbox = this.querySelector('[data-special-instruction-checkbox]');
        this.details = this.querySelector('[data-special-instruction-details]');
        this.textarea = this.querySelector('[data-special-instruction-textarea]');
        this.error = this.querySelector('[data-special-instruction-error]');

        this.onCheckboxChange = this.handleCheckboxChange.bind(this);
        this.onTextareaInput = this.handleTextareaInput.bind(this);

        CartSpecialInstruction.instances.add(this);

        if (this.checkbox) {
          this.checkbox.addEventListener('change', this.onCheckboxChange);
        }

        if (this.textarea) {
          this.resetTextareaAttributes();
          this.textarea.addEventListener('input', this.onTextareaInput);
        }

        this.hideError();

        // Cart page instructions are always available (empty text is allowed).
        if (this.dataset.context === 'page') {
          this.ensureNoteVisible();
        }

        if (!CartSpecialInstruction.checkoutListenerAttached) {
          document.addEventListener('click', CartSpecialInstruction.handleCheckoutClick, true);
          CartSpecialInstruction.checkoutListenerAttached = true;
        }

        CartSpecialInstruction.setupCartUpdateListener();
        CartSpecialInstruction.syncNominationPrompt();
        CartSpecialInstruction.restoreCheckedState();
        CartSpecialInstruction.ensurePageNoteVisible();
      }

      disconnectedCallback() {
        CartSpecialInstruction.instances.delete(this);

        if (this.checkbox) {
          this.checkbox.removeEventListener('change', this.onCheckboxChange);
        }

        if (this.textarea) {
          this.textarea.removeEventListener('input', this.onTextareaInput);
        }
      }

      static forEachInstance(callback) {
        CartSpecialInstruction.instances.forEach(callback);
      }

      static getInstance(context) {
        for (const instance of CartSpecialInstruction.instances) {
          if (instance.dataset.context === context) {
            return instance;
          }
        }

        return CartSpecialInstruction.instances.values().next().value;
      }

      static getNoteValue() {
        for (const instance of CartSpecialInstruction.instances) {
          const value = instance.textarea?.value;

          if (value?.trim()) {
            return value;
          }
        }

        return '';
      }

      isChecked() {
        return CartSpecialInstruction.instructionChecked;
      }

      handleCheckboxChange() {
        if (!CartSpecialInstruction.hasNominationBraceletInCart()) {
          CartSpecialInstruction.clearCheckedState();
          return;
        }

        const isChecked = this.checkbox.checked;

        CartSpecialInstruction.instructionChecked = isChecked;
        sessionStorage.setItem(STORAGE_KEY, String(isChecked));

        CartSpecialInstruction.forEachInstance((instance) => {
          if (instance.checkbox && instance.checkbox !== this.checkbox) {
            instance.checkbox.checked = isChecked;
          }

          instance.resetTextareaAttributes();
          instance.hideError();
        });

        if (isChecked) {
          CartSpecialInstruction.restoreCheckedState({
            focus: true,
            focusContext: this.dataset.context,
          });
          return;
        }

        // Keep cart-page note visible; only collapse drawer note on uncheck.
        CartSpecialInstruction.forEachInstance((instance) => {
          if (instance.dataset.context === 'drawer') {
            instance.collapseAndBlur();
          } else {
            instance.ensureNoteVisible();
          }
        });
      }

      handleTextareaInput() {
        const { value } = this.textarea;

        CartSpecialInstruction.forEachInstance((instance) => {
          const textarea = instance.textarea;

          if (textarea && textarea !== this.textarea && textarea.value !== value) {
            textarea.value = value;
          }
        });

        this.hideError();
      }

      ensureNoteVisible() {
        if (this.details && !this.details.open) {
          this.details.open = true;
        }
      }

      expand() {
        this.ensureNoteVisible();
      }

      collapseAndBlur() {
        if (this.textarea && document.activeElement === this.textarea) {
          this.textarea.blur();
        }

        if (this.details?.open) {
          this.details.open = false;
        }
      }

      resetTextareaAttributes() {
        if (!this.textarea) return;

        this.textarea.removeAttribute('aria-required');
        this.textarea.removeAttribute('aria-invalid');
        this.textarea.removeAttribute('required');
        delete this.textarea.dataset.specialInstructionRequired;
      }

      hideError() {
        if (!this.error) return;

        this.error.hidden = true;
        this.textarea?.removeAttribute('aria-invalid');
      }

      async saveNote() {
        if (typeof routes === 'undefined' || typeof fetchConfig !== 'function') {
          return;
        }

        const note = CartSpecialInstruction.getNoteValue();
        const body = JSON.stringify({ note });

        await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
      }

      static handleCheckoutClick(event) {
        if (!CartSpecialInstruction.instances.size) return;

        const checkoutButton = event.target.closest(CHECKOUT_SELECTOR);
        if (!checkoutButton || checkoutButton.disabled) return;

        const isDrawerCheckout = checkoutButton.closest('cart-drawer, .drawer__footer') !== null;
        const noteValue = CartSpecialInstruction.getNoteValue();

        if (!isDrawerCheckout || !noteValue) return;

        const targetInstance = CartSpecialInstruction.getInstance('drawer');

        event.preventDefault();
        targetInstance?.saveNote().then(() => {
          checkoutButton.form?.requestSubmit(checkoutButton);
        });
      }
    }
  );
}
